import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import JSZip from 'jszip';
import { renderAsync } from 'docx-preview';
import { displayLabel, employeeDisplayName, employeeDesignation, employeeDepartment } from '../../../../utils/displayLabel';
import * as mammoth from 'mammoth/mammoth.browser';
import { employeeService, letterTemplateService, salaryStructureService } from '../../../../services/api';
import { DEFAULT_LETTER_TEMPLATES } from '../../../../utils/defaultLetterTemplates';
import { loadLetterTemplates, saveLetterTemplates } from '../../../../utils/letterTemplatesStorage';
import {
  LETTER_TAG_CATALOG,
  getHypotheticalEmployee as buildHypotheticalEmployee,
  buildLetterMergeContext,
  buildDynamicSalaryTags,
  fetchEmployeeSalaryForLetter,
  getLetterTagValueMap,
  replaceLetterTags,
  applyTagsToDocxBuffer,
  arrayBufferToBase64,
  base64ToArrayBuffer,
} from '../../../../utils/letterTagEngine';
import {
  getSystemSampleTemplate,
  formatMergedLetterToHtml,
} from '../../../../utils/letterDocumentBuilder';
import {
  buildSampleFormatDocxBlob,
  buildDocxBlobFromTemplateText,
  downloadBlobFile,
} from '../../../../utils/letterSampleDocx';
import { getAuthJson } from '../../../../utils/authStorage';
import { DsButton, DsInput } from '../../../../design-system';
import LetterPreviewSendModal from './LetterPreviewSendModal';
import IssuedLettersBulkUploadModal from '../../create-employee/IssuedLettersBulkUploadModal.jsx';

const LETTER_MODAL_Z = 'z-[100]';

function LettersManager() {
  const [activeLetterType, setActiveLetterType] = useState('offer');
  const [showLetterForm, setShowLetterForm] = useState(false);
  const [editingLetter, setEditingLetter] = useState(null);
  /** create | edit | upload — controls modal title when editing an existing template */
  const [templateFormMode, setTemplateFormMode] = useState('create');
  const [templateActionMenu, setTemplateActionMenu] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [letterPreviewPayload, setLetterPreviewPayload] = useState(null);
  const [showIssuedBulkUpload, setShowIssuedBulkUpload] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedTemplateContent, setUploadedTemplateContent] = useState('');
  const [uploadedTemplateHtml, setUploadedTemplateHtml] = useState('');
  const [uploadedTemplatePreviewHtml, setUploadedTemplatePreviewHtml] = useState('');
  const [uploadedTemplateArrayBuffer, setUploadedTemplateArrayBuffer] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [showAvailableTags, setShowAvailableTags] = useState(false);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [previewTemplateData, setPreviewTemplateData] = useState(null);
  const docxPreviewContainerRef = useRef(null);
  const docxStyleContainerRef = useRef(null);
  const [docxRenderFailed, setDocxRenderFailed] = useState(false);
  const [activeTextArea, setActiveTextArea] = useState('content');
  const [showAddLetterType, setShowAddLetterType] = useState(false);
  const [newLetterTypeName, setNewLetterTypeName] = useState('');
  const [searchLetterTypes, setSearchLetterTypes] = useState('');
  const [currentLetterTypePage, setCurrentLetterTypePage] = useState(1);
  const [currentTemplatePage, setCurrentTemplatePage] = useState(1);
  const [searchTemplates, setSearchTemplates] = useState('');
  const [searchAvailableTags, setSearchAvailableTags] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [companySalaryComponents, setCompanySalaryComponents] = useState([]);
  const skipNextPersistRef = useRef(true);

  // Letter types configuration - now dynamic
  const [letterTypes, setLetterTypes] = useState([
    { id: 'offer', name: 'Offer Letter' },
    { id: 'appointment', name: 'Appointment Letter' },
    { id: 'transfer', name: 'Transfer Letter' },
    { id: 'confirmation', name: 'Confirmation Letter' },
    { id: 'experience', name: 'Experience Letter' },
    { id: 'resignation', name: 'Resignation Acceptance Letter' },
    { id: 'promotion', name: 'Promotion Letter' },
    { id: 'salary-increment', name: 'Salary Increment/Revision Letter' },
    { id: 'probation-extension', name: 'Probation Extension Letter' },
    { id: 'termination', name: 'Termination Letter' },
    { id: 'contract', name: 'Contract Letter' },
    { id: 'contract-renewal', name: 'Contract Renewal/Extension Letter' },
    { id: 'other', name: 'Other Company Letters' }
  ]);

  const [letterTemplates, setLetterTemplates] = useState(() =>
    loadLetterTemplates(DEFAULT_LETTER_TEMPLATES)
  );

  // Persist lightly (no fileBase64). Skip first paint to avoid mount thrash.
  useEffect(() => {
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }
    const timer = setTimeout(() => saveLetterTemplates(letterTemplates), 400);
    return () => clearTimeout(timer);
  }, [letterTemplates]);

  // Available tags = catalog + company component tags
  const availableTags = {
    ...LETTER_TAG_CATALOG,
    companyComponents: buildDynamicSalaryTags(companySalaryComponents),
  };

  const sampleEmployees = [
    { id: 'emp001', name: 'John Doe', employeeId: 'EMP001', designation: 'Manager', department: 'IT', reportingManager: 'Sarah Wilson', workLocation: 'Mumbai' },
    { id: 'emp002', name: 'Jane Smith', employeeId: 'EMP002', designation: 'Senior Executive', department: 'HR', reportingManager: 'Mike Johnson', workLocation: 'Delhi' },
    { id: 'emp003', name: 'Mike Johnson', employeeId: 'EMP003', designation: 'Director', department: 'Finance', reportingManager: 'CEO', workLocation: 'Bangalore' }
  ];
  const employeesForGeneration = employeeOptions;

  const companyData = (() => {
    const stored = getAuthJson('companyData', null) || {};
    return {
      name: stored.name || stored.companyName || 'Your Company Name',
      address:
        stored.address ||
        [stored.addressLine1, stored.city, stored.state, stored.country, stored.pincode]
          .filter(Boolean)
          .join(', ') ||
        '123 Business Street, City, State, Country - 12345',
      logo: stored.logo || '',
      phone: stored.phone || '',
      email: stored.email || '',
      website: stored.website || '',
    };
  })();

  useEffect(() => {
    const loadComponents = async () => {
      try {
        const res = await salaryStructureService.listComponents();
        const list = res?.data || res?.components || [];
        setCompanySalaryComponents(Array.isArray(list) ? list : []);
      } catch (error) {
        console.warn('Could not load salary components for letter tags:', error?.message || error);
      }
    };
    loadComponents();
  }, []);

  const fetchEmployeesForLetters = async () => {
    if (employeesLoading) return;
    setEmployeesLoading(true);
    try {
      const response = await employeeService.getEmployees({ page: 1, limit: 500 });
      const list = response?.employees || response?.data || [];
      const normalized = list.map((emp) => ({
        id: String(emp.id || emp._id || emp.empCode || Math.random()),
        name: emp.user?.name || emp.name || emp.fullName || emp.empName || 'Employee',
        employeeId: emp.empCode || emp.employeeId || emp.id || emp._id || 'EMP',
        designation: emp.designation?.name || emp.designationName || emp.designation || '',
        department: emp.department?.name || emp.departmentName || emp.department || '',
        reportingManager:
          [emp.reportingManager?.user?.name, emp.reportingManager?.user?.lastName]
            .filter(Boolean)
            .join(' ')
            .trim() ||
          emp.reportingManager?.name ||
          emp.manager?.name ||
          emp.reportingManagerName ||
          '',
        workLocation: emp.branch?.name || emp.location || emp.workLocation || '',
        company_name: companyData.name,
        rawId: emp.id || emp._id,
        ctc: emp.ctc ?? emp.annualCtc ?? emp.annual_ctc ?? '',
        email: emp.email || emp.officialEmail || '',
        officialEmail: emp.email || emp.officialEmail || '',
        personalEmail: emp.user?.email || emp.personalEmail || '',
        user: emp.user || null,
        joiningDate: emp.dateOfJoining || emp.joiningDate || emp.doj || emp.date_of_joining || '',
        dateOfJoining: emp.dateOfJoining || emp.joiningDate || emp.doj || '',
        probationPeriod: emp.probationPeriod || emp.probation_period || '',
        noticePeriod: emp.noticePeriod || emp.notice_period || '',
        workingHours: emp.workingHours || emp.working_hours || '',
      }));
      setEmployeeOptions(normalized.filter((e) => e.name));
    } catch (error) {
      console.error('Failed to load employees for letter generation:', error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  useEffect(() => {
    const syncFromApi = async () => {
      try {
        const res = await letterTemplateService.list();
        const grouped = res?.data;
        if (grouped && typeof grouped === 'object' && Object.keys(grouped).length > 0) {
          setLetterTemplates((prev) => {
            const next = { ...prev };
            Object.entries(grouped).forEach(([type, list]) => {
              if (!Array.isArray(list) || !list.length) return;
              const existing = Array.isArray(next[type]) ? next[type] : [];
              const byId = new Map(existing.map((t) => [t.id, t]));
              list.forEach((t) => {
                const { fileBase64, ...safe } = t || {};
                byId.set(safe.id, { ...byId.get(safe.id), ...safe });
              });
              next[type] = Array.from(byId.values());
            });
            return next;
          });
        }
      } catch (error) {
        console.warn('Letter templates API sync skipped:', error?.message || error);
      }
    };
    syncFromApi();
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    content: '',
    header: '',
    footer: '',
    companyLogo: '',
    companyName: '',
    fontStyle: 'Arial',
    fontSize: '12',
    fontColor: '#000000',
    assignment: {
      branch: [],
      department: [],
      designation: [],
      grade: [],
      subdepartment: [],
      category: [],
      employmentType: []
    }
  });

  const [showOrgLevelsDropdown, setShowOrgLevelsDropdown] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssignmentChange = (category, value) => {
    setFormData(prev => ({
      ...prev,
      assignment: {
        ...prev.assignment,
        [category]: value
      }
    }));
  };

  const handleSaveTemplate = async () => {
    const resolvedName = (templateName || formData.name || '').trim();
    const defaultTemplate = getDefaultTemplateForType();
    let resolvedContent = (uploadedTemplateContent || formData.content || '').trim();

    if (!resolvedName) {
      alert('Please enter template name');
      return;
    }

    if (!uploadedFile && !editingLetter && !resolvedContent) {
      alert('Please upload a Word template or provide content');
      return;
    }

    // Extract text from docx if content still empty (so save works for Word-only uploads).
    if (!resolvedContent && uploadedTemplateArrayBuffer) {
      try {
        const fallback = await extractDocxTextFallback(uploadedTemplateArrayBuffer);
        resolvedContent = (fallback.text || '').trim();
        if (resolvedContent) {
          setUploadedTemplateContent(resolvedContent);
        }
      } catch (e) {
        console.warn('Could not extract docx text on save:', e);
      }
    }
    if (!resolvedContent) {
      resolvedContent = defaultTemplate?.content || `Dear {{employee_name}},\n\n[Template body for ${resolvedName}]\n\n{{salary_structure_text}}\n\nRegards,\n{{hr_manager_name}}`;
    }

    let fileBase64 = editingLetter?.fileBase64 || '';
    if (uploadedTemplateArrayBuffer) {
      try {
        fileBase64 = await arrayBufferToBase64(uploadedTemplateArrayBuffer);
      } catch (e) {
        console.warn('Could not encode uploaded template:', e);
      }
    }

    const templatePayload = {
      name: resolvedName,
      content: resolvedContent,
      header: formData.header,
      footer: formData.footer,
      companyLogo: formData.companyLogo,
      companyName: formData.companyName,
      fontStyle: formData.fontStyle,
      fontSize: formData.fontSize,
      fontColor: formData.fontColor,
      assignment: formData.assignment,
      uploadedFileName: uploadedFile?.name || editingLetter?.uploadedFileName || '',
      fileBase64: fileBase64 || undefined,
    };

    const templateId = editingLetter?.id || `${activeLetterType}-${Date.now()}`;

    if (editingLetter) {
      setLetterTemplates((prev) => ({
        ...prev,
        [activeLetterType]: (prev[activeLetterType] || []).map((template) =>
          template.id === editingLetter.id ? { ...template, ...templatePayload, id: template.id } : template
        ),
      }));
    } else {
      setLetterTemplates((prev) => ({
        ...prev,
        [activeLetterType]: [...(prev[activeLetterType] || []), { id: templateId, ...templatePayload }],
      }));
    }

    // Persist to backend (DB + uploaded Word file).
    try {
      const saved = await letterTemplateService.save(
        {
          letterType: activeLetterType,
          templateKey: templateId,
          id: templateId,
          name: resolvedName,
          content: resolvedContent,
          header: formData.header || '',
          footer: formData.footer || '',
          assignment: formData.assignment || {},
          companyLogo: formData.companyLogo || '',
          companyName: formData.companyName || '',
          fontStyle: formData.fontStyle || 'Arial',
          fontSize: formData.fontSize || '12',
          fontColor: formData.fontColor || '#000000',
          uploadedFileName: uploadedFile?.name || editingLetter?.uploadedFileName || '',
        },
        uploadedFile || undefined
      );
      const savedRow = saved?.data || saved;
      if (savedRow && (savedRow.dbId || savedRow.id || savedRow.filePath)) {
        setLetterTemplates((prev) => ({
          ...prev,
          [activeLetterType]: (prev[activeLetterType] || []).map((t) =>
            t.id === templateId
              ? {
                  ...t,
                  dbId: savedRow.dbId || savedRow.id || t.dbId,
                  filePath: savedRow.filePath || t.filePath,
                  uploadedFileName: savedRow.uploadedFileName || t.uploadedFileName,
                  // Keep in-memory Word bytes for generate (not written to localStorage)
                  fileBase64: fileBase64 || t.fileBase64,
                }
              : t
          ),
        }));
      }
    } catch (error) {
      console.warn('Backend letter template save failed (kept locally):', error?.message || error);
    }

    setFormData({
      name: '',
      content: '',
      header: '',
      footer: '',
      companyLogo: '',
      companyName: '',
      fontStyle: 'Arial',
      fontSize: '12',
      fontColor: '#000000',
      assignment: {
        branch: [],
        department: [],
        designation: [],
        grade: [],
        subdepartment: [],
        category: [],
        employmentType: [],
      },
    });
    setShowLetterForm(false);
    setEditingLetter(null);
    setTemplateFormMode('create');
    setShowAvailableTags(false);
    setUploadedFile(null);
    setUploadedTemplateContent('');
    setUploadedTemplateHtml('');
    setUploadedTemplatePreviewHtml('');
    setUploadedTemplateArrayBuffer(null);
    setTemplateName('');
  };

  const loadTemplateIntoForm = (template, formMode = 'edit') => {
    setTemplateFormMode(formMode);
    setEditingLetter(template);
    setUploadedFile(null);
    setUploadedTemplateContent('');
    setUploadedTemplateHtml('');
    setUploadedTemplatePreviewHtml('');
    setUploadedTemplateArrayBuffer(null);
    setTemplateName(template.name || '');
    setShowAvailableTags(true);
    setFormData({
      name: template.name,
      content: template.content || '',
      header: template.header || '',
      footer: template.footer || '',
      companyLogo: template.companyLogo || '',
      companyName: template.companyName || '',
      fontStyle: template.fontStyle || 'Arial',
      fontSize: template.fontSize || '12',
      fontColor: template.fontColor || '#000000',
      assignment: template.assignment || {
        branch: [],
        department: [],
        designation: [],
        grade: [],
        subdepartment: [],
        category: [],
        employmentType: [],
      },
    });
    setShowLetterForm(true);
  };

  const openTemplateActionMenu = (e, template) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 200;
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
    const top = rect.bottom + 4;
    setTemplateActionMenu((prev) =>
      prev?.template?.id === template.id ? null : { template, top, left }
    );
  };

  useEffect(() => {
    if (!templateActionMenu) return undefined;
    const close = () => setTemplateActionMenu(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [templateActionMenu]);

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    setLetterTemplates((prev) => ({
      ...prev,
      [activeLetterType]: (prev[activeLetterType] || []).filter((template) => template.id !== templateId),
    }));
    try {
      await letterTemplateService.remove(templateId);
    } catch (error) {
      console.warn('Backend letter template delete failed:', error?.message || error);
    }
  };

  const handleCopyTemplate = (template) => {
    const copiedTemplate = {
      ...template,
      id: `${activeLetterType}-${Date.now()}`,
      name: `${template.name} (Copy)`,
    };

    setLetterTemplates((prev) => ({
      ...prev,
      [activeLetterType]: [copiedTemplate, ...(prev[activeLetterType] || [])],
    }));
  };

  const handleDownloadTemplate = (template) => {
    const blob = new Blob([template.content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const replaceTags = (content, employee, options) =>
    replaceLetterTags(content, employee, options);

  const getHypotheticalEmployee = (companyNameOverride) =>
    buildHypotheticalEmployee({
      name: typeof companyNameOverride === 'string' ? companyNameOverride : companyData.name,
      address: companyData.address,
      ...(typeof companyNameOverride === 'object' && companyNameOverride ? companyNameOverride : {}),
    });

  const getTagValueMap = (employeeData) =>
    getLetterTagValueMap(employeeData, { preferPlainSalaryTable: true });

  const escapeHtml = (text = '') =>
    String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const toPreviewHtml = (text = '') => escapeHtml(text).replace(/\n/g, '<br/>');

  const extractDocxTextFallback = async (arrayBuffer) => {
    try {
      const zip = await JSZip.loadAsync(arrayBuffer);
      const decodeXmlEntities = (input = '') =>
        input
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&apos;/g, "'");

      const collectTextFromXml = async (path) => {
        const file = zip.file(path);
        if (!file) return '';
        const xml = await file.async('text');

        // Primary: regex paragraph extraction for namespace-tolerant parsing.
        const paragraphRegex = /<w:p\b[\s\S]*?<\/w:p>/g;
        const textRegex = /<(w:t|a:t|w:instrText)\b[^>]*>([\s\S]*?)<\/\1>/g;
        const paragraphMatches = xml.match(paragraphRegex) || [];

        if (paragraphMatches.length > 0) {
          const lines = paragraphMatches.map((p) => {
            const parts = [];
            let m;
            while ((m = textRegex.exec(p)) !== null) {
              parts.push(decodeXmlEntities(m[2] || ''));
            }
            return parts.join('');
          });
          const joined = lines.join('\n').trim();
          if (joined) return joined;
        }

        // Secondary: plain text-node extraction across whole XML.
        const allParts = [];
        let m;
        while ((m = textRegex.exec(xml)) !== null) {
          allParts.push(decodeXmlEntities(m[2] || ''));
        }
        return allParts.join(' ').trim();
      };

      const docText = await collectTextFromXml('word/document.xml');
      const headerFiles = Object.keys(zip.files).filter((k) => /^word\/header\d+\.xml$/.test(k));
      const footerFiles = Object.keys(zip.files).filter((k) => /^word\/footer\d+\.xml$/.test(k));

      const headerTextParts = await Promise.all(headerFiles.map((f) => collectTextFromXml(f)));
      const footerTextParts = await Promise.all(footerFiles.map((f) => collectTextFromXml(f)));

      const headerText = headerTextParts.filter(Boolean).join('\n');
      const footerText = footerTextParts.filter(Boolean).join('\n');
      const combined = [headerText, docText, footerText].filter(Boolean).join('\n\n').trim();

      return {
        text: combined,
        html: combined ? toPreviewHtml(combined) : '',
      };
    } catch (e) {
      console.error('DOCX fallback text extraction failed:', e);
      return { text: '', html: '' };
    }
  };

  useEffect(() => {
    const renderDocxPreview = async () => {
      if (!showTemplatePreview || !previewTemplateData?.useDocxRender) return;
      if (!uploadedTemplateArrayBuffer || !docxPreviewContainerRef.current) return;

      try {
        setDocxRenderFailed(false);
        docxPreviewContainerRef.current.innerHTML = '';
        const valueMap = previewTemplateData?.tagValueMap || {};
        // Merge split Word runs + replace tags in XML before render (fixes raw {{tags}}).
        const taggedBuffer =
          Object.keys(valueMap).length > 0
            ? await applyTagsToDocxBuffer(uploadedTemplateArrayBuffer, valueMap)
            : uploadedTemplateArrayBuffer;
        await renderAsync(
          taggedBuffer,
          docxPreviewContainerRef.current,
          docxStyleContainerRef.current || docxPreviewContainerRef.current,
          {
            className: 'docx-preview',
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            breakPages: true,
            renderHeaders: true,
            renderFooters: true,
            renderFootnotes: true,
            renderEndnotes: true,
            useBase64URL: true,
          }
        );

        // Safety pass for any tags that survived in contiguous text nodes.
        if (Object.keys(valueMap).length > 0) {
          const walker = document.createTreeWalker(
            docxPreviewContainerRef.current,
            NodeFilter.SHOW_TEXT
          );
          const textNodes = [];
          let node = walker.nextNode();
          while (node) {
            textNodes.push(node);
            node = walker.nextNode();
          }
          textNodes.forEach((textNode) => {
            if (!textNode?.nodeValue) return;
            let updated = textNode.nodeValue;
            Object.entries(valueMap).forEach(([tag, val]) => {
              updated = updated.split(tag).join(String(val));
            });
            updated = updated.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, name) =>
              valueMap[`{{${name}}}`] != null ? String(valueMap[`{{${name}}}`]) : `[${name}]`
            );
            if (updated !== textNode.nodeValue) {
              textNode.nodeValue = updated;
            }
          });
        }

        const renderedText = (docxPreviewContainerRef.current.textContent || '').trim();
        if (!renderedText) {
          setDocxRenderFailed(true);
        }
      } catch (error) {
        console.error('DOCX preview rendering failed:', error);
        setDocxRenderFailed(true);
      }
    };

    renderDocxPreview();
  }, [showTemplatePreview, previewTemplateData, uploadedTemplateArrayBuffer]);

  const enrichEmployeeForLetter = (employee, salaryRows = [], structure = null) =>
    buildLetterMergeContext({
      company: companyData,
      employee: employee || {},
      salaryRows: Array.isArray(salaryRows) ? salaryRows : [],
      structure,
    });

  /** Load salary + hydrate profile fields (manager, CTC, designation) for tag merge. */
  const loadSalaryForEmployee = async (employee) => {
    const empKey = employee?.rawId || employee?.id;
    if (!empKey) return { rows: [], structure: null, warning: 'No employee id', employeeCtc: null, employee };

    let hydrated = { ...employee };
    try {
      const detail = await employeeService.getEmployee(empKey);
      const empDetail = detail?.employee || detail?.data || detail;
      if (empDetail && typeof empDetail === 'object') {
        const rm =
          empDetail.reportingManager?.user?.name ||
          [empDetail.reportingManager?.user?.name, empDetail.reportingManager?.user?.lastName]
            .filter(Boolean)
            .join(' ') ||
          empDetail.reportingManager?.name ||
          empDetail.reportingManagerName ||
          '';
        const rmFull = [
          empDetail.reportingManager?.user?.name || empDetail.reportingManager?.name,
          empDetail.reportingManager?.user?.lastName || empDetail.reportingManager?.lastName,
        ]
          .filter(Boolean)
          .join(' ')
          .trim();
        hydrated = {
          ...hydrated,
          ...empDetail,
          name:
            hydrated.name ||
            empDetail.user?.name ||
            [empDetail.user?.name, empDetail.user?.lastName].filter(Boolean).join(' ') ||
            empDetail.name,
          employeeId: hydrated.employeeId || empDetail.empCode || empDetail.employeeId,
          empCode: empDetail.empCode || hydrated.employeeId,
          designation:
            empDetail.designation?.name ||
            empDetail.designationName ||
            hydrated.designation ||
            '',
          department:
            empDetail.department?.name ||
            empDetail.departmentName ||
            hydrated.department ||
            '',
          workLocation:
            empDetail.branch?.name ||
            empDetail.workLocation ||
            hydrated.workLocation ||
            '',
          reportingManager: rmFull || rm || hydrated.reportingManager || '',
          reportingManagerName: rmFull || rm || hydrated.reportingManagerName || '',
          ctc: empDetail.ctc ?? hydrated.ctc,
          dateOfJoining: empDetail.dateOfJoining || hydrated.dateOfJoining,
          joiningDate: empDetail.dateOfJoining || hydrated.joiningDate,
          probationPeriod: empDetail.probationPeriod || hydrated.probationPeriod,
          noticePeriod: empDetail.noticePeriod || hydrated.noticePeriod,
          rawId: empDetail.id || hydrated.rawId || empKey,
        };

        // If manager still blank but we have an id, resolve from reporting-employees list
        if (
          !hydrated.reportingManager &&
          (empDetail.reportingManagerId || empDetail.reporting_manager_id)
        ) {
          try {
            const mgrId = empDetail.reportingManagerId || empDetail.reporting_manager_id;
            const mgrList = await employeeService.getReportingEmployees?.();
            const list = mgrList?.employees || mgrList?.data || mgrList || [];
            const mgr = (Array.isArray(list) ? list : []).find(
              (m) => String(m.id) === String(mgrId) || String(m.userId) === String(mgrId)
            );
            if (mgr) {
              hydrated.reportingManager =
                [mgr.user?.name || mgr.name, mgr.user?.lastName || mgr.lastName]
                  .filter(Boolean)
                  .join(' ')
                  .trim() ||
                mgr.empCode ||
                '';
            }
          } catch (e) {
            console.warn('Could not resolve reporting manager name:', e?.message || e);
          }
        }
      }
    } catch (e) {
      console.warn('Could not hydrate employee for letter:', e?.message || e);
    }

    const result = await fetchEmployeeSalaryForLetter(empKey, {
      getActiveStructure: (id) => salaryStructureService.getActiveStructure(id),
      listEmployeeStructures: (id) => salaryStructureService.listEmployeeStructures(id),
    });
    if (result.warning) {
      console.warn('Letter salary load:', result.warning);
    }
    return { ...result, employeeCtc: hydrated.ctc, employee: hydrated };
  };

  /** Load uploaded Word bytes for a saved template (memory → API). */
  const loadTemplateDocxBuffer = async (template) => {
    if (!template) return null;
    if (template.fileBase64) {
      try {
        return base64ToArrayBuffer(template.fileBase64);
      } catch (e) {
        console.warn('Invalid template fileBase64:', e);
      }
    }
    const key = template.dbId || template.id;
    if (!key) return null;
    if (!template.filePath && !template.uploadedFileName && !template.dbId) {
      // System text sample — no Word file on server
      return null;
    }
    try {
      const blob = await letterTemplateService.downloadFile(key);
      if (!blob || blob.size === 0) return null;
      // API may return JSON error as blob
      if (blob.type && blob.type.includes('application/json')) return null;
      return await blob.arrayBuffer();
    } catch (e) {
      console.warn('Could not download template Word file:', e?.message || e);
      return null;
    }
  };

  /**
   * Framework: preserve Word format; only replace {{tags}}.
   * Download filled .docx — never rewrite layout with HTML letter chrome.
   */
  const handleGenerateLetter = async (template, employee) => {
    if (!template || !employee) {
      alert('Select a template and employee first.');
      return;
    }
    try {
      const { rows, structure, warning, employee: hydrated } = await loadSalaryForEmployee(employee);
      const sourceEmp = hydrated || employee;
      const emp = enrichEmployeeForLetter(sourceEmp, rows, structure);
      const tagValueMap = getLetterTagValueMap(emp, {
        preferPlainSalaryTable: true,
        forDocx: true,
        salaryRows: rows,
      });

      if (!emp.total_ctc && !emp.gross_salary) {
        alert(
          `No salary data found for ${sourceEmp.employeeId || sourceEmp.name}.\n\n` +
            `Assign an active salary structure (Employee → Salary Structure), or set Annual CTC on the employee profile, then generate again.`
        );
        return;
      }
      if (!rows.length) {
        const proceed = window.confirm(
          `${sourceEmp.employeeId || sourceEmp.name} has Annual CTC but no active salary structure breakup.\n\n` +
            `The letter will include CTC / monthly figures. Component-wise Annexure A needs an assigned salary structure.\n\n` +
            `${warning ? `Note: ${warning}\n\n` : ''}Continue to PDF preview?`
        );
        if (!proceed) return;
      }

      let buffer = await loadTemplateDocxBuffer(template);
      let outBlob;
      const safeName = `${(template.name || 'Letter').replace(/[^\w\- ]+/g, '')}-${sourceEmp.employeeId || sourceEmp.name || 'employee'}`;

      if (buffer) {
        const filled = await applyTagsToDocxBuffer(buffer, tagValueMap);
        outBlob = new Blob([filled], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
      } else {
        const merged = replaceLetterTags(
          [template.header, template.content, template.footer].filter(Boolean).join('\n\n'),
          emp,
          { preferPlainSalaryTable: true }
        );
        outBlob = await buildDocxBlobFromTemplateText({ bodyText: merged });
        if (!outBlob) {
          alert('This template has no Word file or content to generate.');
          return;
        }
      }

      const arrayBuffer = await outBlob.arrayBuffer();
      setShowGenerateModal(false);
      setLetterPreviewPayload({
        employee: {
          ...sourceEmp,
          email: sourceEmp.email || sourceEmp.officialEmail || '',
          officialEmail: sourceEmp.officialEmail || sourceEmp.email || '',
          personalEmail: sourceEmp.personalEmail || sourceEmp.user?.email || '',
        },
        letterName: template.name || 'Letter',
        letterType: template.letterType || activeLetterType || 'appointment',
        templateKey: template.id || template.templateKey || null,
        fileBaseName: safeName,
        docxBlob: outBlob,
        docxArrayBuffer: arrayBuffer,
      });
    } catch (error) {
      console.error('Letter generate failed:', error);
      alert(error?.message || 'Failed to generate letter. Ensure a .docx template is uploaded.');
    }
  };

  const handlePreviewLetter = async (template, employee) => {
    const keepTags = !employee;
    const loaded = employee
      ? await loadSalaryForEmployee(employee)
      : { rows: [], structure: null, employee: {} };
    const { rows, structure, employee: hydrated } = loaded;
    const previewEmployee = enrichEmployeeForLetter(hydrated || employee || {}, rows, structure);
    const tagValueMap = keepTags
      ? {}
      : getLetterTagValueMap(previewEmployee, {
          preferPlainSalaryTable: true,
          forDocx: true,
          salaryRows: rows,
        });

    // Prefer exact Word preview when a .docx exists
    let previewBuffer = null;
    if (!keepTags) {
      previewBuffer = await loadTemplateDocxBuffer(template);
      if (previewBuffer && Object.keys(tagValueMap).length) {
        try {
          previewBuffer = await applyTagsToDocxBuffer(previewBuffer, tagValueMap);
        } catch (e) {
          console.warn('Tag apply for preview failed:', e);
        }
      }
    } else if (template.uploadedFileName || template.filePath || template.fileBase64) {
      previewBuffer = await loadTemplateDocxBuffer(template);
    }

    if (previewBuffer) {
      setUploadedTemplateArrayBuffer(previewBuffer);
      setPreviewTemplateData({
        ...template,
        name: template.name,
        content: '',
        contentHtml: '',
        richContentHtml: '',
        useDocxRender: true,
        previewMode: employee ? 'generated' : 'template',
        // Tags already applied into buffer — avoid second pass wiping values
        tagValueMap: {},
        uploadedFileName: template.uploadedFileName || `${template.name || 'letter'}.docx`,
      });
      setDocxRenderFailed(false);
      setShowTemplatePreview(true);
      return;
    }

    // Fallback: plain text body only (no letterhead chrome)
    const raw = keepTags
      ? [template.header, template.content, template.footer].filter(Boolean).join('\n\n')
      : replaceLetterTags(
          [template.header, template.content, template.footer].filter(Boolean).join('\n\n'),
          previewEmployee,
          { preferPlainSalaryTable: true }
        );
    let bodyHtml = formatMergedLetterToHtml(raw);
    if (keepTags) {
      bodyHtml = bodyHtml.replace(
        /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g,
        '<code style="font-family:Consolas,monospace;background:#f3f4f6;padding:1px 4px;border-radius:3px;">{{$1}}</code>'
      );
    }

    setPreviewTemplateData({
      ...template,
      name: template.name,
      content: raw,
      contentHtml: bodyHtml,
      richContentHtml: `<div style="max-width:800px;margin:0 auto;padding:24px;font-family:'Times New Roman',Times,serif;font-size:12.5px;line-height:1.55;">${bodyHtml}</div>`,
      useDocxRender: false,
      previewMode: 'template',
      tagValueMap,
      uploadedFileName: template.uploadedFileName || '',
    });
    setDocxRenderFailed(false);
    setShowTemplatePreview(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name || '';
    const isWordByType =
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'application/msword';
    const isWordByName = /\.(docx|doc)$/i.test(fileName);

    if (!isWordByType && !isWordByName) {
      alert('Please upload a valid Word document (.doc or .docx)');
      return;
    }

    setUploadedFile(file);
    setUploadedTemplateContent('');
    setUploadedTemplateHtml('');
    setUploadedTemplatePreviewHtml('');
    setUploadedTemplateArrayBuffer(null);
    setTemplateName((prev) => prev || fileName.replace(/\.(docx|doc)$/i, ''));

    try {
      if (/\.docx$/i.test(fileName)) {
        const arrayBuffer = await file.arrayBuffer();
        setUploadedTemplateArrayBuffer(arrayBuffer);
        // Keep text/html fallback optional; primary preview is exact DOCX render.
        setUploadedTemplateContent('');
        setUploadedTemplateHtml('');
        try {
          const result = await mammoth.convertToHtml(
            { arrayBuffer },
            {
              convertImage: mammoth.images.inline(async (image) => ({
                src: await image.read('base64').then((data) => `data:${image.contentType};base64,${data}`),
              })),
            }
          );
          setUploadedTemplatePreviewHtml(result?.value || '');
        } catch (htmlErr) {
          console.warn('Mammoth HTML conversion failed:', htmlErr);
        }
      } else {
        alert('Exact preview is supported for .docx format. Please upload the file in .docx format.');
      }
    } catch (err) {
      console.error('Failed to read uploaded file buffer', err);
      alert('Unable to read the uploaded file buffer. Please try uploading again.');
    }
  };

  const getDefaultTemplateForType = () => {
    const system = getSystemSampleTemplate(activeLetterType);
    if (system) return system;
    const templates = letterTemplates[activeLetterType] || [];
    return templates.find((t) => String(t.id).endsWith('-1')) || templates[0] || null;
  };

  const handleDownloadSampleFormat = async () => {
    const typeName = letterTypes.find((t) => t.id === activeLetterType)?.name || activeLetterType;
    const sampleTemplate = getDefaultTemplateForType();
    if (!sampleTemplate) {
      alert('No sample format available for this letter type.');
      return;
    }

    try {
      const blob = await buildSampleFormatDocxBlob({
        letterType: activeLetterType,
        typeLabel: typeName,
        template: sampleTemplate,
      });
      if (!blob) {
        alert('Unable to build sample document for this letter type.');
        return;
      }
      downloadBlobFile(blob, `${typeName}-Sample-Format.docx`);
    } catch (error) {
      console.error('Sample .docx download failed:', error);
      alert('Failed to download sample as .docx. Please try again.');
    }
  };

  const handleUploadedTemplatePreview = async () => {
    if (!uploadedFile) {
      alert('Please upload a Word template first.');
      return;
    }

    // Keep {{tags}} visible — this is a format check, not employee fill.
    const tagValueMap = {};

    let previewBaseContent = uploadedTemplateContent || '';
    let previewBaseHtml = uploadedTemplateHtml || '';

    let canUseDocxRender = false;
    let previewArrayBuffer = uploadedTemplateArrayBuffer;
    const uploadedFileName = uploadedFile?.name || '';

    if (/\.docx$/i.test(uploadedFileName) && uploadedFile) {
      try {
        previewArrayBuffer = await uploadedFile.arrayBuffer();
        setUploadedTemplateArrayBuffer(previewArrayBuffer);
        canUseDocxRender = true;

        if (!previewBaseContent && !previewBaseHtml && previewArrayBuffer) {
          const fallback = await extractDocxTextFallback(previewArrayBuffer);
          previewBaseContent = fallback.text || '';
          previewBaseHtml = fallback.html || '';
          setUploadedTemplateContent(previewBaseContent);
          setUploadedTemplateHtml(previewBaseHtml);
        }
      } catch (e) {
        console.error('Failed to read uploaded docx buffer for preview:', e);
        canUseDocxRender = false;
      }
    } else if (uploadedTemplateArrayBuffer) {
      canUseDocxRender = true;
    }

    const previewData = {
      id: `upload-preview-${Date.now()}`,
      name: templateName || uploadedFile.name.replace(/\.(docx|doc)$/i, '') || 'Uploaded Template',
      content: previewBaseContent,
      contentHtml: previewBaseHtml || toPreviewHtml(previewBaseContent),
      richContentHtml: uploadedTemplatePreviewHtml || '',
      useDocxRender: canUseDocxRender,
      previewMode: 'uploaded',
      tagValueMap,
      header: '',
      footer: '',
      uploadedFileName: uploadedFile?.name || '',
    };

    setDocxRenderFailed(false);
    setPreviewTemplateData(previewData);
    setShowTemplatePreview(true);
  };

  const handleTemplateCardPreview = (template) => {
    handlePreviewLetter(template, null);
  };

  const insertTag = (tag) => {
    const element = document.getElementById(activeTextArea);
    if (element) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(tag);
        range.insertNode(textNode);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // If no selection, append at the end
        element.innerHTML += tag;
      }
      
      // Update form data
      setFormData(prev => ({
        ...prev,
        [activeTextArea]: element.innerHTML
      }));
    }
  };

  const applyFormatting = (format, value) => {
    const element = document.getElementById(activeTextArea);
    if (element) {
      // Focus the element first
      element.focus();
      
      if (format === 'pagebreak') {
        // Insert page break at cursor position
        const pageBreak = document.createElement('div');
        pageBreak.style.pageBreakBefore = 'always';
        pageBreak.style.height = '0';
        pageBreak.style.margin = '0';
        pageBreak.style.padding = '0';
        pageBreak.innerHTML = '<br>';
        
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.insertNode(pageBreak);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        
        // Update form data
        setFormData(prev => ({
          ...prev,
          [activeTextArea]: element.innerHTML
        }));
      } else if (format === 'fontStyle' || format === 'fontSize') {
        // Apply to entire content
        element.style.fontFamily = format === 'fontStyle' ? value : element.style.fontFamily;
        element.style.fontSize = format === 'fontSize' ? `${value}px` : element.style.fontSize;
        
        setFormData(prev => ({
          ...prev,
          [activeTextArea]: element.innerHTML
        }));
      } else {
        // Use document.execCommand for better MS Word-like behavior
        let success = false;
        
        switch (format) {
          case 'bold':
            success = document.execCommand('bold', false, null);
            break;
          case 'italic':
            success = document.execCommand('italic', false, null);
            break;
          case 'underline':
            success = document.execCommand('underline', false, null);
            break;
          case 'color': {
            const color = value || prompt('Enter color (e.g., #FF0000 or blue):', '#0000FF');
            if (color) {
              success = document.execCommand('foreColor', false, color);
            } else {
              return;
            }
            break;
          }
          case 'justifyLeft':
            success = document.execCommand('justifyLeft', false, null);
            break;
          case 'justifyCenter':
            success = document.execCommand('justifyCenter', false, null);
            break;
          case 'justifyRight':
            success = document.execCommand('justifyRight', false, null);
            break;
          case 'justifyFull':
            success = document.execCommand('justifyFull', false, null);
            break;
          default:
            return;
        }
        
        if (success) {
          // Update form data after successful formatting
          setFormData(prev => ({
            ...prev,
            [activeTextArea]: element.innerHTML
          }));
        } else if (format !== 'justifyLeft' && format !== 'justifyCenter' && format !== 'justifyRight' && format !== 'justifyFull') {
          alert('Please select text to format');
        }
      }
    }
  };

  const handleAddLetterType = () => {
    if (!newLetterTypeName.trim()) {
      alert('Please enter a letter type name');
      return;
    }
    
    const newType = {
      id: newLetterTypeName.toLowerCase().replace(/\s+/g, '-'),
      name: newLetterTypeName,
    };
    
    setLetterTypes(prev => [...prev, newType]);
    setLetterTemplates(prev => ({
      ...prev,
      [newType.id]: []
    }));
    setNewLetterTypeName('');
    setShowAddLetterType(false);
  };

  const getFilteredLetterTypes = () => {
    return letterTypes.filter(type => 
      type.name.toLowerCase().includes(searchLetterTypes.toLowerCase())
    );
  };

  const getPaginatedLetterTypes = () => {
    const filtered = getFilteredLetterTypes();
    const startIndex = (currentLetterTypePage - 1) * 5;
    return filtered.slice(startIndex, startIndex + 5);
  };

  const getFilteredTemplates = () => {
    const templates = letterTemplates[activeLetterType] || [];
    return templates.filter(template => 
      template.name.toLowerCase().includes(searchTemplates.toLowerCase())
    );
  };

  const getPaginatedTemplates = () => {
    const filtered = getFilteredTemplates();
    const startIndex = (currentTemplatePage - 1) * 5;
    return filtered.slice(startIndex, startIndex + 5);
  };

  const getFilteredAvailableTags = () => {
    if (!searchAvailableTags.trim()) {
      return availableTags;
    }
    
    const filtered = {};
    Object.entries(availableTags).forEach(([category, tags]) => {
      const filteredTags = tags.filter(({ tag, description }) => 
        tag.toLowerCase().includes(searchAvailableTags.toLowerCase()) ||
        description.toLowerCase().includes(searchAvailableTags.toLowerCase())
      );
      if (filteredTags.length > 0) {
        filtered[category] = filteredTags;
      }
    });
    return filtered;
  };

  return (
    <div className="space-y-6">
      <style jsx>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9CA3AF;
          font-style: italic;
        }
        [contenteditable]:focus:before {
          content: none;
        }
        [contenteditable] {
          outline: none;
          direction: ltr !important;
          text-align: left !important;
          unicode-bidi: normal !important;
        }
        [contenteditable] * {
          margin: 0;
          padding: 0;
          direction: ltr !important;
          text-align: left !important;
          unicode-bidi: normal !important;
        }
        [contenteditable] p {
          margin: 0;
          padding: 0;
          direction: ltr !important;
          text-align: left !important;
        }
        [contenteditable] br {
          line-height: 1.6;
        }
        [contenteditable] strong, [contenteditable] b {
          direction: ltr !important;
          text-align: left !important;
        }
        [contenteditable] em, [contenteditable] i {
          direction: ltr !important;
          text-align: left !important;
        }
        [contenteditable] u {
          direction: ltr !important;
          text-align: left !important;
        }
        [contenteditable] span {
          direction: ltr !important;
          text-align: left !important;
        }
      `}</style>
      <div className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)] lg:grid lg:grid-cols-3">
        {/* Left Side - Letter Types */}
        <aside className="border-b border-[hsl(var(--ds-border))] p-4 lg:border-b-0 lg:border-r">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">Letter Types</h3>
            <DsButton type="button" variant="outline" size="sm" onClick={() => setShowAddLetterType(true)}>
              + Add New
            </DsButton>
          </div>

          <div className="mb-3">
            <DsInput
              type="text"
              placeholder="Search letter types..."
              value={searchLetterTypes}
              onChange={(e) => {
                setSearchLetterTypes(e.target.value);
                setCurrentLetterTypePage(1);
              }}
            />
          </div>

          <div className="max-h-80 space-y-1 overflow-y-auto">
            {getPaginatedLetterTypes().map((type) => (
              <button
                key={type.id}
                type="button"
                onClick={() => {
                  setActiveLetterType(type.id);
                  setCurrentTemplatePage(1);
                  setSearchTemplates('');
                }}
                className={
                  activeLetterType === type.id
                    ? 'ds-page-nav-item ds-page-nav-item-active'
                    : 'ds-page-nav-item'
                }
              >
                <div className="font-medium">{type.name}</div>
                <div className="ds-caption mt-0.5 text-[hsl(var(--ds-muted-fg))]">
                  {letterTemplates[type.id]?.length || 0} templates
                </div>
              </button>
            ))}
          </div>

          {getFilteredLetterTypes().length > 5 && (
            <div className="mt-4 flex items-center justify-between border-t border-[hsl(var(--ds-border))] pt-3">
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCurrentLetterTypePage((prev) => Math.max(1, prev - 1))}
                disabled={currentLetterTypePage === 1}
              >
                Previous
              </DsButton>
              <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                Page {currentLetterTypePage} of {Math.ceil(getFilteredLetterTypes().length / 5)}
              </span>
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentLetterTypePage((prev) =>
                    Math.min(Math.ceil(getFilteredLetterTypes().length / 5), prev + 1)
                  )
                }
                disabled={currentLetterTypePage >= Math.ceil(getFilteredLetterTypes().length / 5)}
              >
                Next
              </DsButton>
            </div>
          )}
        </aside>

        {/* Right Side - Templates and Actions */}
        <div className="p-4 lg:col-span-2 lg:p-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">
              {letterTypes.find((t) => t.id === activeLetterType)?.name} Templates
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowIssuedBulkUpload(true)}
              >
                Upload Issued Letters
              </DsButton>
              <DsButton
                type="button"
                variant="primary"
                size="sm"
                onClick={() => {
                  setTemplateFormMode('create');
                  setEditingLetter(null);
                  setUploadedFile(null);
                  setUploadedTemplateContent('');
                  setUploadedTemplateHtml('');
                  setUploadedTemplatePreviewHtml('');
                  setUploadedTemplateArrayBuffer(null);
                  setTemplateName('');
                  setShowAvailableTags(true);
                  setFormData((prev) => ({
                    ...prev,
                    name: '',
                    content: '',
                    header: '',
                    footer: '',
                  }));
                  setShowLetterForm(true);
                }}
              >
                Create New Template
              </DsButton>
            </div>
          </div>

          <div className="mb-3">
            <DsInput
              type="text"
              placeholder="Search templates..."
              value={searchTemplates}
              onChange={(e) => {
                setSearchTemplates(e.target.value);
                setCurrentTemplatePage(1);
              }}
            />
          </div>

          <div className="max-h-96 space-y-2 overflow-y-auto">
            {getPaginatedTemplates().map((template) => (
              <div
                key={template.id}
                className="flex items-center justify-between gap-3 border-b border-[hsl(var(--ds-border))] py-3 last:border-b-0"
              >
                <h4 className="ds-body min-w-0 flex-1 truncate font-medium text-[hsl(var(--ds-fg))]">
                  {template.name}
                </h4>
                <button
                  type="button"
                  aria-label="Template actions"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  onClick={(e) => openTemplateActionMenu(e, template)}
                >
                  <MoreVertical size={16} />
                </button>
              </div>
            ))}

            {getFilteredTemplates().length === 0 && (
              <div className="py-8 text-center ds-body text-[hsl(var(--ds-muted-fg))]">
                {searchTemplates
                  ? 'No templates found matching your search'
                  : 'No templates available for this letter type'}
              </div>
            )}
          </div>

          {getFilteredTemplates().length > 5 && (
            <div className="mt-4 flex items-center justify-between border-t border-[hsl(var(--ds-border))] pt-4">
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCurrentTemplatePage((prev) => Math.max(1, prev - 1))}
                disabled={currentTemplatePage === 1}
              >
                Previous
              </DsButton>
              <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                Page {currentTemplatePage} of {Math.ceil(getFilteredTemplates().length / 5)}
              </span>
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentTemplatePage((prev) =>
                    Math.min(Math.ceil(getFilteredTemplates().length / 5), prev + 1)
                  )
                }
                disabled={currentTemplatePage >= Math.ceil(getFilteredTemplates().length / 5)}
              >
                Next
              </DsButton>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Template Modal */}
      {showLetterForm && (
        <div className={`fixed inset-0 ${LETTER_MODAL_Z} flex items-center justify-center bg-black/50`}>
          <div className="flex max-h-[92vh] w-[92vw] max-w-5xl flex-col rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
            <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] p-6">
              <h2 className="ds-h3 text-[hsl(var(--ds-fg))]">
                {templateFormMode === 'upload'
                  ? 'Upload Template Format'
                  : editingLetter
                    ? 'Edit Template'
                    : 'Add Template Format'}
              </h2>
              <DsButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowLetterForm(false);
                  setEditingLetter(null);
                  setTemplateFormMode('create');
                  setShowAvailableTags(false);
                  setUploadedFile(null);
                  setUploadedTemplateContent('');
                  setUploadedTemplateHtml('');
                  setUploadedTemplatePreviewHtml('');
                  setUploadedTemplateArrayBuffer(null);
                  setTemplateName('');
                }}
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </DsButton>
            </div>

            <div className="space-y-6 overflow-y-auto p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="ds-caption mb-1.5 block text-[hsl(var(--ds-muted-fg))]">Template Name *</label>
                  <DsInput
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Enter template name"
                  />
                </div>
                <div>
                  <label className="ds-caption mb-1.5 block text-[hsl(var(--ds-muted-fg))]">Upload MS Word File *</label>
                  <input
                    type="file"
                    accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileUpload}
                    className="ds-input h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none file:mr-3 file:rounded file:border-0 file:bg-[hsl(var(--ds-muted))] file:px-2 file:py-1 file:text-xs"
                  />
                </div>
              </div>

              <div className="rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 p-3 ds-body text-[hsl(var(--ds-muted-fg))]">
                {uploadedFile
                  ? `Uploaded: ${uploadedFile.name}`
                  : editingLetter?.uploadedFileName
                    ? `Current uploaded file: ${editingLetter.uploadedFileName}`
                    : 'No Word file uploaded yet. Download Sample Format (.docx), keep {{tags}} in Word, then upload.'}
              </div>

              <div className="flex flex-wrap gap-2">
                <DsButton type="button" variant="outline" size="sm" onClick={handleDownloadSampleFormat}>
                  Download Sample Format
                </DsButton>
                <DsButton type="button" variant="outline" size="sm" onClick={handleUploadedTemplatePreview}>
                  Preview Uploaded Format
                </DsButton>
                <DsButton type="button" variant="ghost" size="sm" onClick={() => setShowAvailableTags((v) => !v)}>
                  {showAvailableTags ? 'Hide Available Tags' : 'Show Available Tags'}
                </DsButton>
              </div>

              {showAvailableTags && (
                <div className="rounded-md border border-[hsl(var(--ds-border))] p-4">
                  <p className="ds-caption mb-3 text-[hsl(var(--ds-muted-fg))]">
                    Click a tag to copy it into your Word document (e.g. {'{{employee_name}}'}, {'{{salary_structure_table}}'}).
                  </p>
                  <div className="mb-4">
                    <DsInput
                      type="text"
                      placeholder="Search tags..."
                      value={searchAvailableTags}
                      onChange={(e) => setSearchAvailableTags(e.target.value)}
                    />
                  </div>
                  <div className="grid max-h-72 grid-cols-1 gap-4 overflow-y-auto md:grid-cols-2">
                    {Object.entries(getFilteredAvailableTags()).map(([category, tags]) =>
                      tags?.length ? (
                        <div key={category}>
                          <h4 className="ds-caption mb-2 capitalize text-[hsl(var(--ds-fg))]">{category}</h4>
                          <div className="space-y-1">
                            {tags.map(({ tag, description }) => (
                              <button
                                type="button"
                                key={tag}
                                title="Click to copy tag"
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(tag);
                                  } catch {
                                    /* ignore */
                                  }
                                }}
                                className="w-full rounded border border-[hsl(var(--ds-border))] px-2 py-1 text-left text-xs text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                              >
                                <code className="mr-1 rounded bg-[hsl(var(--ds-muted))] px-1">{tag}</code>
                                <span className="text-[hsl(var(--ds-muted-fg))]">{description}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : null
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-[hsl(var(--ds-border))] p-6">
              <DsButton
                type="button"
                variant="outline"
                onClick={() => {
                  setShowLetterForm(false);
                  setEditingLetter(null);
                  setTemplateFormMode('create');
                  setShowAvailableTags(false);
                  setUploadedFile(null);
                  setUploadedTemplateContent('');
                  setUploadedTemplateHtml('');
                  setUploadedTemplatePreviewHtml('');
                  setUploadedTemplateArrayBuffer(null);
                  setTemplateName('');
                }}
              >
                Cancel
              </DsButton>
              <DsButton type="button" variant="primary" onClick={handleSaveTemplate}>
                {editingLetter ? 'Update Template' : 'Save Template'}
              </DsButton>
            </div>
          </div>
        </div>
      )}

      {/* Generate Letter Modal */}
      {showGenerateModal && (
        <div className={`fixed inset-0 ${LETTER_MODAL_Z} flex items-center justify-center bg-black/50`}>
          <div className="w-[90vw] max-w-2xl rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
            <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] p-6">
              <h2 className="ds-h3 text-[hsl(var(--ds-fg))]">
                Generate - {letterTypes.find((t) => t.id === activeLetterType)?.name}
              </h2>
              <DsButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowGenerateModal(false);
                  setSelectedEmployee(null);
                  setSelectedTemplate(null);
                }}
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </DsButton>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="ds-caption mb-1.5 block text-[hsl(var(--ds-muted-fg))]">
                    Select Template
                  </label>
                  <select
                    value={selectedTemplate?.id || ''}
                    onChange={(e) => {
                      const template = letterTemplates[activeLetterType]?.find((t) => t.id === e.target.value);
                      setSelectedTemplate(template);
                    }}
                    className="ds-input h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                  >
                    <option value="">Select a template</option>
                    {letterTemplates[activeLetterType]?.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="ds-caption mb-1.5 block text-[hsl(var(--ds-muted-fg))]">
                    Select Employee
                  </label>
                  <select
                    value={selectedEmployee?.id || ''}
                    onChange={(e) => {
                      const employee = employeesForGeneration.find((emp) => String(emp.id) === String(e.target.value));
                      setSelectedEmployee(employee);
                    }}
                    className="ds-input h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                  >
                    <option value="">
                      {employeesLoading
                        ? 'Loading employees…'
                        : employeesForGeneration.length > 0
                          ? 'Select an employee'
                          : 'No employees available'}
                    </option>
                    {employeesForGeneration.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employeeDisplayName(employee)} - {employeeDesignation(employee)} ({employeeDepartment(employee)})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="ds-caption mb-1.5 block text-[hsl(var(--ds-muted-fg))]">
                    Email Address
                  </label>
                  <DsInput type="email" placeholder="Enter email address" />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[hsl(var(--ds-border))] p-6">
              <DsButton
                type="button"
                variant="outline"
                disabled={!selectedTemplate || !selectedEmployee}
                onClick={() => {
                  if (selectedTemplate && selectedEmployee) {
                    handlePreviewLetter(selectedTemplate, selectedEmployee);
                    setShowGenerateModal(false);
                  }
                }}
              >
                Preview Letter
              </DsButton>

              <div className="flex gap-3">
                <DsButton
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowGenerateModal(false);
                    setSelectedEmployee(null);
                    setSelectedTemplate(null);
                  }}
                >
                  Cancel
                </DsButton>
                <DsButton
                  type="button"
                  variant="primary"
                  disabled={!selectedTemplate || !selectedEmployee}
                  onClick={() => {
                    if (selectedTemplate && selectedEmployee) {
                      handleGenerateLetter(selectedTemplate, selectedEmployee);
                      setShowGenerateModal(false);
                      setSelectedEmployee(null);
                      setSelectedTemplate(null);
                    }
                  }}
                >
                  Generate & Send PDF
                </DsButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {showTemplatePreview && previewTemplateData && (
        <div className={`fixed inset-0 ${LETTER_MODAL_Z} flex items-center justify-center bg-black/50`}>
          <div className="flex h-[95vh] w-[98vw] max-w-[1400px] flex-col rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
            <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)] px-6 py-4">
              <h2 className="ds-h3 text-[hsl(var(--ds-fg))]">
                Template Preview - {previewTemplateData.name}
              </h2>
              <DsButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowTemplatePreview(false);
                  setPreviewTemplateData(null);
                }}
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </DsButton>
            </div>

            <div className="border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-6 py-3">
              <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                {previewTemplateData.previewMode === 'uploaded'
                  ? `Word Preview Mode: ${previewTemplateData.useDocxRender ? 'Enabled (.docx)' : 'Unavailable'}`
                  : 'Template Preview Mode: Merged Template Content'}
              </p>
              {docxRenderFailed && (
                <p className="ds-caption mt-1 text-[hsl(var(--ds-warning-fg))]">
                  Exact Word renderer returned blank output for this file. Showing fallback preview content.
                </p>
              )}
            </div>

            <div className="flex-1 overflow-hidden bg-[hsl(var(--ds-muted))]">
              <div className="h-full overflow-auto">
                {previewTemplateData.useDocxRender && !docxRenderFailed ? (
                  <>
                    <div ref={docxStyleContainerRef} />
                    <div ref={docxPreviewContainerRef} className="min-h-full bg-[hsl(var(--ds-surface))] p-6" />
                  </>
                ) : (
                  <div className="p-8">
                    {!previewTemplateData.useDocxRender && (
                      <div className="mb-4 max-w-xl rounded-lg border border-[hsl(var(--ds-warning)/0.4)] bg-[hsl(var(--ds-surface))] p-4">
                        <p className="mb-1 font-medium text-[hsl(var(--ds-warning-fg))]">Exact Word preview is available only for .docx uploads</p>
                        <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
                          Please upload a `.docx` file to preview the same layout with tables, numbering, fonts, header, footer, and logo.
                        </p>
                      </div>
                    )}
                    <div
                      className="overflow-auto rounded border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-6"
                      style={{ minHeight: '700px', lineHeight: '1.6' }}
                      dangerouslySetInnerHTML={{
                        __html:
                          previewTemplateData.richContentHtml ||
                          previewTemplateData.contentHtml ||
                          `<div style="white-space: pre-wrap;">${previewTemplateData.content || ''}</div>`
                      }}
                    />
                    {!previewTemplateData.contentHtml && !previewTemplateData.content && (
                      <div className="mt-3 ds-body text-[hsl(var(--ds-muted-fg))]">
                        No readable preview content was extracted from this file.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4">
              <DsButton
                type="button"
                variant="outline"
                onClick={() => {
                  setShowTemplatePreview(false);
                  setPreviewTemplateData(null);
                }}
              >
                Close
              </DsButton>
            </div>
          </div>
        </div>
      )}

      {/* Add New Letter Type Modal */}
      {showAddLetterType && (
        <div className={`fixed inset-0 ${LETTER_MODAL_Z} flex items-center justify-center bg-black/50`}>
          <div className="w-96 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
            <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] p-6">
              <h2 className="ds-h3 text-[hsl(var(--ds-fg))]">Add New Letter Type</h2>
              <DsButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowAddLetterType(false);
                  setNewLetterTypeName('');
                }}
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </DsButton>
            </div>

            <div className="p-6">
              <div>
                <label className="ds-caption mb-1.5 block text-[hsl(var(--ds-muted-fg))]">
                  Letter Type Name
                </label>
                <DsInput
                  type="text"
                  value={newLetterTypeName}
                  onChange={(e) => setNewLetterTypeName(e.target.value)}
                  placeholder="Enter letter type name"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-[hsl(var(--ds-border))] p-6">
              <DsButton
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddLetterType(false);
                  setNewLetterTypeName('');
                }}
              >
                Cancel
              </DsButton>
              <DsButton type="button" variant="primary" onClick={handleAddLetterType}>
                Add Letter Type
              </DsButton>
            </div>
          </div>
        </div>
      )}

      {templateActionMenu &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[10018]" aria-hidden onClick={() => setTemplateActionMenu(null)} />
            <div
              className="fixed z-[10019] w-52 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-1 text-sm shadow-lg"
              style={{ top: templateActionMenu.top, left: templateActionMenu.left }}
              role="menu"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                onClick={() => {
                  setSelectedTemplate(templateActionMenu.template);
                  setShowGenerateModal(true);
                  fetchEmployeesForLetters();
                  setTemplateActionMenu(null);
                }}
              >
                Generate
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                onClick={() => {
                  handleTemplateCardPreview(templateActionMenu.template);
                  setTemplateActionMenu(null);
                }}
              >
                Preview
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                onClick={() => {
                  loadTemplateIntoForm(templateActionMenu.template, 'edit');
                  setTemplateActionMenu(null);
                }}
              >
                Edit
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                onClick={() => {
                  handleCopyTemplate(templateActionMenu.template);
                  setTemplateActionMenu(null);
                }}
              >
                Copy
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                onClick={() => {
                  loadTemplateIntoForm(templateActionMenu.template, 'upload');
                  setTemplateActionMenu(null);
                }}
              >
                Upload Template
              </button>
              <button
                type="button"
                className="w-full border-t border-[hsl(var(--ds-border))] px-3 py-2 text-left text-[hsl(var(--ds-primary))] hover:bg-rose-50"
                onClick={() => {
                  handleDeleteTemplate(templateActionMenu.template.id);
                  setTemplateActionMenu(null);
                }}
              >
                Delete
              </button>
            </div>
          </>,
          document.body
        )}

      {letterPreviewPayload && (
        <LetterPreviewSendModal
          open
          employee={letterPreviewPayload.employee}
          letterName={letterPreviewPayload.letterName}
          letterType={letterPreviewPayload.letterType}
          templateKey={letterPreviewPayload.templateKey}
          fileBaseName={letterPreviewPayload.fileBaseName}
          docxBlob={letterPreviewPayload.docxBlob}
          docxArrayBuffer={letterPreviewPayload.docxArrayBuffer}
          onClose={() => setLetterPreviewPayload(null)}
        />
      )}

      <IssuedLettersBulkUploadModal
        open={showIssuedBulkUpload}
        onClose={() => setShowIssuedBulkUpload(false)}
      />

    </div>
  );
}

export default LettersManager;
export { LettersManager };
